import { PageRoute } from "@web-src/features/app/types";
import { useTranslations, cx } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Buffer } from "buffer";
import { PlainButton } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { ChatMessagePayloadConference } from "@jugl-web/rest-api";
import { ReactComponent as LogoIcon } from "./assets/logo.svg";
import { ReactComponent as VideoMeetingIcon } from "./assets/video-meeting.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as VideoCameraIncomingIcon } from "./assets/video-camera-incoming.svg";
import { ReactComponent as VideoCameraOutgoingIcon } from "./assets/video-camera-outgoing.svg";
import { MessageTokensDisplay } from "../MessageTokensDisplay";
import { tokenizeMessageBody } from "../../utils";

export const VideoMeetingAttachment: React.FC<{
  conference: ChatMessagePayloadConference;
  isOutgoing?: boolean;
  isPreview?: boolean;
  isTile?: boolean;
  tileText?: string;
  className?: string;
  onCancel?: () => void;
}> = ({
  conference,
  isPreview,
  isOutgoing,
  isTile,
  tileText,
  className,
  onCancel,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();

  const base64 = useMemo(() => {
    const base64JSON = Buffer.from(
      JSON.stringify(conference),
      "utf-8"
    ).toString("base64");
    return base64JSON;
  }, [conference]);

  const subtitle = useMemo(() => {
    if (isTile && tileText) {
      return <MessageTokensDisplay tokens={tokenizeMessageBody(tileText)} />;
    }

    if (conference.date && conference.time) {
      return `${t({
        id: "chats-page.starts-on",
        defaultMessage: "Starts on",
      })}: ${conference.date}, ${conference.time}`;
    }

    return undefined;
  }, [conference.date, isTile, tileText, t, conference.time]);

  const $content = (
    <div
      className={cx(
        "flex flex-col",
        isPreview && !isTile ? "mb-[18px] gap-[18px]" : "gap-3",
        className
      )}
    >
      <div className={cx("flex gap-2", !isPreview && "ml-1")}>
        <div className="bg-primary-100 w-0.5 shrink-0 rounded-full" />
        <div className="flex flex-col gap-0.5 overflow-hidden">
          <span className={cx(isTile ? "truncate" : "jugl__break-word")}>
            {isOutgoing ? (
              <VideoCameraOutgoingIcon className="mr-1 align-middle" />
            ) : (
              <VideoCameraIncomingIcon className="mr-1 align-middle" />
            )}
            <span
              className={cx(
                "font-secondary text-sm italic leading-[21px]",
                isOutgoing ? "text-white" : "text-dark"
              )}
            >
              {conference.name ||
                t({
                  id: "chats-page.join-video-meeting",
                  defaultMessage: "Join video meeting",
                })}
            </span>
          </span>
          {subtitle && (
            <span
              className={cx(
                "font-secondary leading-2 text-xs italic",
                isOutgoing ? "text-primary-50" : "text-grey-900",
                isTile ? "truncate" : "jugl__break-word"
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {!isTile && (
        <div className="relative flex max-w-[300px] flex-col overflow-hidden rounded-lg shadow">
          {onCancel && (
            <PlainButton
              onClick={onCancel}
              className="bg-primary-400 absolute top-2 right-2 flex h-[24px] w-[24px] items-center justify-center rounded-full hover:brightness-90"
            >
              <CloseIcon />
            </PlainButton>
          )}
          <div
            className={cx(
              "flex flex-col gap-0.5 py-2.5 px-4",
              isPreview ? "bg-primary-600" : "bg-primary-400"
            )}
          >
            <LogoIcon />
            <span className="text-primary-50 font-secondary text-[13px] leading-[140%]">
              {conference?.time
                ? t({
                    id: "chats-page.call-bubble.scheduled-video-meeting",
                    defaultMessage: "Scheduled video meeting",
                  })
                : t({
                    id: "chats-page.join-video-meeting",
                    defaultMessage: "Join video meeting",
                  })}
            </span>
          </div>
          <VideoMeetingIcon className="bg-white" />
        </div>
      )}
    </div>
  );

  if (isPreview) {
    return <div>{$content}</div>;
  }
  return (
    <Link
      to={{
        pathname: `/${entityId}/${PageRoute.conference}/${conference?.channel}`,
        search: `?i=${base64}`,
      }}
      className="cursor-pointer no-underline"
      target="_blank"
    >
      {$content}
    </Link>
  );
};
